import { useAuthStoreShallow } from "@/features/authentication";
import { useGetFoldersQuery } from "@/shared/services";
import { useLockedAction } from "@components/plan-subscription-lock";
import Icon from "@components/ui/icon";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { IconChevronRight, IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { toast } from "react-toastify";
import { CreateFolderDialog } from "./create-folder-dialog";
import { FoldersList } from "./folders-list";

const FEATURE = "stories-folders-limit";

export const Folders = () => {
  const [isOpen, setIsOpen] = useState(true);
  const { data: folders } = useGetFoldersQuery();
  const { onLockedAction, value, plan } = useLockedAction({
    feature: FEATURE,
  });
  const hasLimitation = typeof value === "number";
  const isLocked =
    hasLimitation &&
    folders?.length &&
    folders?.length >= value &&
    value !== null;

  return (
    <div className="tw-pb-1">
      <div className="tw-pl-8">
        <button
          type="button"
          className="tw-flex tw-h-7 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-font-sans tw-text-xs tw-font-normal tw-text-blue/70 tw-outline-none tw-transition-opacity hover:tw-opacity-100"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>
            Folders{" "}
            <span
              className={cn(
                plan === "pro" && "tw-text-pro-base",
                plan === "team" && "tw-text-team-base",
              )}
            >
              {hasLimitation && `(${folders?.length}/${value})`}
            </span>
          </span>

          <IconChevronRight
            className={cn("tw-size-4 tw-text-blue tw-transition-transform", {
              "tw-rotate-90": isOpen,
            })}
          />
        </button>
      </div>
      <div
        className={cn("tw-grid tw-transition-[grid-template-rows] tw-pl-6", {
          "[grid-template-rows:1fr]": isOpen,
          "[grid-template-rows:0fr]": !isOpen,
        })}
      >
        <div className="tw-overflow-hidden">
          <div className="tw-mb-1 tw-pt-1.5">
            <AddNewFolder
              isLocked={!!isLocked}
              onLockedAction={onLockedAction}
              plan={plan}
            />
            <FoldersList />
          </div>
        </div>
      </div>
    </div>
  );
};

const AddNewFolder = ({
  isLocked,
  onLockedAction,
  plan,
}: {
  isLocked: boolean;
  onLockedAction: () => void;
  plan: string;
}) => {
  const isUserOrCreatorLite = useAuthStoreShallow((s) => s.isUserOrCreatorLite);
  const dispatch = useAppDispatch();
  const handleClick = () => {
    if (isUserOrCreatorLite) {
      toast.info("Only administrators and creators can create a new folder.", {
        toastId: "create-folder-toast",
      });
      return;
    }
    if (isLocked) return onLockedAction();
    dispatch(
      openDialog({
        id: CreateFolderDialog.id,
        content: CreateFolderDialog,
        props: {},
      }),
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="tw-flex tw-h-7 tw-w-full tw-cursor-pointer tw-items-center tw-gap-2 tw-rounded-md tw-pl-2 tw-pr-1 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-outline-none tw-transition-colors hover:tw-bg-blue-100"
    >
      <IconPlus className="tw-size-4 tw-text-blue" />
      New folder
      {isLocked && <PlanLockedLabel plan={plan} />}
    </button>
  );
};

const PlanLockedLabel = ({ plan }: { plan: string }) => {
  return (
    <span
      style={{ boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.10)" }}
      className={cn(
        "tw-ml-auto tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-md",
        {
          "tw-bg-pro-base": plan === "pro",
          "tw-bg-team-base": plan === "team",
        },
      )}
    >
      <Icon
        icon="ri-vip-crown-fill"
        className="tw-size-3 tw-text-xs/none tw-text-white"
      />
    </span>
  );
};
